import classNames from "classnames";
import { useWindowSize } from "hooks/windowSize";
import { useEffect, useState } from "react";
import Draggable, { DraggableEventHandler } from "react-draggable";
import { Resizable, ResizeCallbackData } from "react-resizable";
import styled from "styled-components";
import { AspectBounds, Coordinates, Dimensions, MinDimensions } from "types/Positioning";
import { WidgetData, WidgetPosition } from "types/Widget";
import {
  clampDimensions,
  getAspectDimensions,
  getPercentagePosition,
  getPixelDimensions,
  getPixelPosition,
  getSpotlightSize,
} from "util/positioning";

import { SpotlightAction } from "./actions";
import {
  PreventDragClass,
  WidgetCloseSpotlight,
  WidgetRemoveHandle,
  WidgetResizeHandle,
  WidgetSettingsHandle,
} from "./WidgetComponents";
import { WidgetQuickActions } from "./WidgetQuickAction";
import { AllWidgetTypes } from "./widgets";

export const WidgetClass: string = "widget";

export interface WidgetProps extends WidgetData {
  setPosition: (position: Partial<WidgetPosition>) => void;
  setState: (state: object, override?: boolean) => void;
  remove: () => void;
  bringToFront: () => void;
  spotlightWidget: () => void;
  isFocused: boolean;
  isSpotlight: boolean;
  openSettings: () => void;
  updateSettings: (setting: string, data: any) => void;
  onToggleFocus: (isFocus: boolean, widgetId: string) => void;
  isSettingsOpen: boolean;
}

const Widget: React.FC<WidgetProps> = ({
  id,
  typeId,
  position,
  state,
  setPosition,
  setState,
  remove,
  bringToFront,
  spotlightWidget,
  isFocused,
  isSpotlight,
  openSettings,
  settings,
  updateSettings,
  onToggleFocus,
  isSettingsOpen,
}) => {
  const screenSize = useWindowSize();
  const minDimensions: MinDimensions = AllWidgetTypes[typeId].sizing;
  const aspectBounds: AspectBounds = AllWidgetTypes[typeId].sizing;
  const { aspectRatio } = AllWidgetTypes[typeId].defaultSize;
  const { component, quickActions } = AllWidgetTypes[typeId];
  const Component = component;

  const [pixelPosition, setPixelPosition] = useState<Coordinates>(
    getPixelPosition(position, screenSize),
  );
  const [resizingSize, setResizingSize] = useState<Dimensions>(
    getPixelDimensions(position, screenSize),
  );
  const [pixelSize, setPixelSize] = useState<Dimensions>(getPixelDimensions(position, screenSize));
  const [disableDraggable, setDisableDraggable] = useState(false);

  //update position when resizing
  useEffect(() => {
    setPixelPosition(getPixelPosition(position, screenSize));
  }, [position, screenSize, aspectBounds]);

  //update scale when resizing
  useEffect(() => {
    const size = clampDimensions(
      getPixelDimensions(position, screenSize),
      pixelPosition,
      minDimensions,
      screenSize,
      aspectBounds,
    );
    setPixelSize(isSpotlight ? getSpotlightSize(screenSize, size) : size);
    setResizingSize(size);
  }, [position, screenSize, aspectBounds, pixelPosition, minDimensions, isSpotlight]);

  const onDragStop: DraggableEventHandler = (_, data) => {
    setPixelPosition({ x: data.x, y: data.y });

    //update underlying data
    setPosition(getPercentagePosition(data, screenSize));
  };

  const onDragStart: DraggableEventHandler = (e, _) => {
    bringToFront();

    //prevent dragging if there's anything in the heirarchy with the 'no-drag' classname
    if (e.target instanceof Element) {
      if (e.target.closest(`.${PreventDragClass}`)) {
        return false;
      }
      //  for text editor
      if (Boolean(e.target.closest(`.ql-toolbar`)) || Boolean(e.target.closest(`.ql-container`))) {
        return false;
      }
    }
  };

  function onResizeStart(e: React.SyntheticEvent, _data: ResizeCallbackData) {
    e.stopPropagation();
    bringToFront();
  }

  function onResize(_e: React.SyntheticEvent, data: ResizeCallbackData) {
    setResizingSize(data.size);
    setPixelSize(
      clampDimensions(data.size, pixelPosition, minDimensions, screenSize, aspectBounds),
    );
  }

  function onResizeEnd(_e: React.SyntheticEvent, data: ResizeCallbackData) {
    const clampedSize = clampDimensions(
      data.size,
      pixelPosition,
      minDimensions,
      screenSize,
      aspectBounds,
    );
    setResizingSize(clampedSize);
    setPixelSize(clampedSize);

    //update underlying data
    setPosition(getAspectDimensions(clampedSize, screenSize));
  }

  const dashboardActions = {
    bringToFront: bringToFront,
    spotlightWidget: spotlightWidget,
  };

  return (
    <Draggable
      position={pixelPosition}
      bounds={{
        left: -(resizingSize.width || 0) * 0.9,
        top: -(resizingSize.height || 0) * 0.95,
        right: (screenSize.width || 0) - (resizingSize.width || 0) * 0.05,
        bottom: (screenSize.height || 0) - (resizingSize.height || 0) * 0.05,
      }}
      onStart={onDragStart}
      onStop={onDragStop}
      disabled={disableDraggable}
    >
      <Resizable
        width={resizingSize.width || 0}
        height={resizingSize.height || 0}
        onResize={onResize}
        onResizeStart={onResizeStart}
        onResizeStop={onResizeEnd}
        handle={<WidgetResizeHandle handleAxis="se" />}
      >
        <WidgetContainer
          onClick={bringToFront}
          style={{
            zIndex: position.zIndex,
            visibility: pixelPosition.x === 0 ? "hidden" : "visible",
          }}
          isFocused={isFocused}
          isSpotlight={isSpotlight}
          {...pixelSize}
          {...minDimensions}
          defaultAspect={aspectRatio}
          spotlightType={
            pixelSize.width! / screenSize.width! > pixelSize.height! / screenSize.height!
              ? "width"
              : "height"
          }
        >
          <WidgetInnerContainer
            background={
              typeId === "image" && state.activeImage ? "transparent" : "var(--widget-bg)"
            }
            borderColor={
              typeId === "image" && state.activeImage ? "transparent" : "var(--widget-color)"
            }
            boxShadow={typeId === "image" && state.activeImage ? "none" : "0 0 3px 0"}
          >
            <Component
              widgetId={id}
              pixelPosition={{ ...pixelPosition, ...pixelSize }}
              state={state}
              setState={setState}
              settings={settings}
              setSetting={updateSettings}
              onToggleFocus={onToggleFocus}
              isSettingsOpen={isSettingsOpen}
              onOpenSettings={openSettings}
              onClickPicker={(value) => setDisableDraggable(value)}
            />
          </WidgetInnerContainer>
          <WidgetSettingsHandle onClick={openSettings} />
          <WidgetRemoveHandle onClick={remove} />
          <WidgetCloseSpotlight onClick={spotlightWidget} />
          <WidgetQuickActions
            actions={{ spotlight: SpotlightAction, ...quickActions }}
            quickActionData={settings}
            setQuickActionData={updateSettings}
            dashboardActions={dashboardActions}
            widgetTypeId={typeId}
            widgetState={state}
            setWidgetState={setState}
          />
        </WidgetContainer>
      </Resizable>
    </Draggable>
  );
};

const WidgetInnerContainer = styled.div.attrs<{
  background: boolean;
  borderColor: string;
  boxShadow: string;
}>(() => ({
  className: classNames("widget-inner-container"),
}))<{ background: string; borderColor: string; boxShadow: string }>`
  background: ${({ background }) => background};
  box-sizing: border-box;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  border-radius: var(--widget-border-radius);
  border: 1px solid var(--widget-colour);
  user-select: none;
  border-color: ${({ borderColor }) => borderColor};
  box-shadow: ${({ boxShadow }) => boxShadow};
`;

interface WidgetContainerProps extends Dimensions, MinDimensions {
  defaultAspect: number;
  isFocused: boolean;
  isSpotlight: boolean;
  spotlightType: "width" | "height";
}

//Setting any CSS that doesn't directly change based on props
//  This is done separately so it's not re-built every time the widget changes
const WidgetContainerBase = styled.div`
  box-sizing: border-box;
  position: absolute;
  container-type: size;
  container-name: widget;
  cursor: move;

  min-width: var(--widget-min-width);
  min-height: var(--widget-min-height);

  width: var(--widget-width);
  height: var(--widget-height);
`;

//Variables are directly set on the object so that we aren't creating classes
//every time it changes
const WidgetContainer = styled(WidgetContainerBase).attrs<WidgetContainerProps>(
  ({
    minWidth,
    minHeight,
    width = 1,
    height = 1,
    defaultAspect,
    isFocused,
    isSpotlight,
    spotlightType,
  }) => ({
    style: {
      "--widget-min-width": `${minWidth || 0}px`,
      "--widget-min-height": `${minHeight || 0}px`,

      "--widget-width": `${width}px`,
      "--widget-height": `${height}px`,
      "--default-aspect": `${defaultAspect}`,
      "--current-aspect": `${width / height}`,
    },
    className: classNames(
      WidgetClass,
      isFocused && !isSpotlight && "focused",
      isSpotlight && "spotlight",
      isSpotlight && "no-drag",
      spotlightType === "width" ? "use-width" : "use-height",
    ),
  }),
)<WidgetContainerProps>`
  &.focused .widget-inner-container {
    border-color: var(--widget-colour);
    box-shadow: 0 0 3px 0;
  }
`;

export { Widget };
